import { useEffect, useMemo, useState } from 'react';
import { useApp } from '@/App';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BadgeCheck, Building2, Mail, MapPin, Phone, School, UserRound, CheckCircle2, AlertCircle } from 'lucide-react';
import { schoolAdminService } from '@/services/schoolAdminService';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface SchoolAdminProfileForm {
  fullName: string;
  email: string;
  phone: string;
  campusName: string;
  district: string;
  timezone: string;
}

interface SchoolAdminProfileProps {
  darkMode?: boolean;
}

export function SchoolAdminProfile({ darkMode: dm = false }: SchoolAdminProfileProps) {
  const { user } = useApp();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [formData, setFormData] = useState<SchoolAdminProfileForm>({
    fullName: user?.name || 'School Administrator',
    email: user?.email || '',
    phone: '',
    campusName: '',
    district: '',
    timezone: 'America/New_York',
  });

  useEffect(() => {
    schoolAdminService.getProfile().then(p => {
      setFormData({
        fullName: p.name || user?.name || 'School Administrator',
        email: p.email || user?.email || '',
        phone: p.phone || '',
        campusName: p.campusName || '',
        district: p.district || '',
        timezone: p.timezone || 'America/New_York',
      });
    }).catch(() => {/* keep defaults */});
  }, [user]);

  const initials = useMemo(() => {
    const parts = formData.fullName.split(' ').filter(Boolean);
    return parts.map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'AD';
  }, [formData.fullName]);

  const updateField = (field: keyof SchoolAdminProfileForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    setSaveStatus('saving');
    try {
      await schoolAdminService.saveProfile({
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        campusName: formData.campusName,
        district: formData.district,
        timezone: formData.timezone,
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const cardCls    = `rounded-xl border ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls   = dm ? 'text-white' : 'text-slate-800';
  const descCls    = dm ? 'text-white' : 'text-slate-500';
  const labelCls   = dm ? 'text-white' : '';
  const inputCls   = dm ? 'bg-slate-700 border-slate-600 text-white placeholder:text-white' : '';
  const bodyTxtCls = dm ? 'text-white' : 'text-slate-700';
  const mutedCls   = dm ? 'text-white' : 'text-slate-400';
  const snapshotRow = dm ? 'text-white' : 'text-slate-700';

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-dm={dm}>

      {/* Profile header */}
      <div className={`${cardCls} p-6`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className={`h-20 w-20 rounded-2xl text-2xl font-bold flex items-center justify-center shrink-0 ${dm ? 'bg-blue-600/20 ring-1 ring-blue-500/30 text-blue-400' : 'bg-blue-50 ring-1 ring-blue-200 text-blue-700'}`}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`text-xl font-bold ${titleCls}`}>{formData.fullName}</h2>
            <p className={`text-sm ${descCls}`}>{formData.email}</p>
            <div className="flex items-center gap-4 mt-2">
              <div className={`flex items-center gap-1.5 text-xs font-medium ${dm ? 'text-emerald-400' : 'text-emerald-700'}`}>
                <BadgeCheck className="h-4 w-4" />
                <span>Verified administrator</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${dm ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'}`}>
                School Admin
              </span>
            </div>
          </div>
          <div className="hidden sm:flex flex-col items-end text-right gap-1">
            <p className={`text-xs ${mutedCls}`}>School ID</p>
            <p className={`text-xs font-mono ${bodyTxtCls}`}>{user?.schoolId || 'N/A'}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Settings form */}
        <div className={`lg:col-span-2 ${cardCls} p-6 space-y-6`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-base font-semibold ${titleCls}`}>Profile Settings</h3>
              <p className={`text-sm mt-0.5 ${descCls}`}>Update administrator and campus metadata</p>
            </div>
            {saveStatus === 'saved' && (
              <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${dm ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'}`}>
                <CheckCircle2 className="h-3.5 w-3.5" /> Saved
              </span>
            )}
            {saveStatus === 'error' && (
              <span className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${dm ? 'bg-red-900/40 text-red-400' : 'bg-red-50 text-red-700 ring-1 ring-red-200'}`}>
                <AlertCircle className="h-3.5 w-3.5" /> Save failed
              </span>
            )}
          </div>

          <div className={`border-t ${dm ? 'border-slate-700' : 'border-slate-100'}`} />

          <div>
            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${mutedCls}`}>Personal Information</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className={`text-xs font-medium ${labelCls}`}>Full name</Label>
                <Input id="name" value={formData.fullName} onChange={(e) => updateField('fullName', e.target.value)} className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className={`text-xs font-medium ${labelCls}`}>Email</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => updateField('email', e.target.value)} className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone" className={`text-xs font-medium ${labelCls}`}>Phone</Label>
                <Input id="phone" value={formData.phone} onChange={(e) => updateField('phone', e.target.value)} className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <Label className={`text-xs font-medium ${labelCls}`}>Timezone</Label>
                <Select value={formData.timezone} onValueChange={(value) => updateField('timezone', value)}>
                  <SelectTrigger className={`btn-glass btn-glass-pill ${dm ? 'text-slate-200' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className={`border-t ${dm ? 'border-slate-700' : 'border-slate-100'}`} />

          <div>
            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${mutedCls}`}>Campus Details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="campus" className={`text-xs font-medium ${labelCls}`}>Campus name</Label>
                <Input id="campus" value={formData.campusName} onChange={(e) => updateField('campusName', e.target.value)} className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="district" className={`text-xs font-medium ${labelCls}`}>District</Label>
                <Input id="district" value={formData.district} onChange={(e) => updateField('district', e.target.value)} className={inputCls} />
              </div>
            </div>
          </div>

          <div className={`border-t ${dm ? 'border-slate-700' : 'border-slate-100'}`} />

          <div className="flex items-center gap-3">
            <button onClick={onSave} disabled={saveStatus === 'saving'} className="btn-glass btn-glass-pill btn-glass-blue disabled:opacity-60">
              {saveStatus === 'saving' ? 'Saving…' : 'Save Changes'}
            </button>
            <button
              onClick={() =>
                setFormData({
                  fullName: user?.name || 'School Administrator',
                  email: user?.email || '',
                  phone: '',
                  campusName: '',
                  district: '',
                  timezone: 'America/New_York',
                })
              }
              className="btn-glass btn-glass-pill"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Right sidebar */}
        <div className="space-y-6">
          {/* Campus snapshot */}
          <div className={`${cardCls} p-5`}>
            <h3 className={`text-sm font-semibold mb-1 ${titleCls}`}>Campus Snapshot</h3>
            <p className={`text-xs mb-4 ${descCls}`}>Quick school profile details</p>
            <div className="space-y-3">
              {[
                { icon: UserRound, text: formData.fullName },
                { icon: Mail,      text: formData.email },
                { icon: Phone,     text: formData.phone },
                { icon: School,    text: formData.campusName },
                { icon: Building2, text: formData.district },
                { icon: MapPin,    text: `School ID: ${user?.schoolId || 'N/A'}` },
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${dm ? 'bg-slate-700' : 'bg-slate-50'}`}>
                    <Icon className="h-3.5 w-3.5 text-blue-500" />
                  </div>
                  <span className={`text-sm truncate ${snapshotRow}`}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Account info */}
          <div className={`${cardCls} p-5`}>
            <h3 className={`text-sm font-semibold mb-3 ${titleCls}`}>Account Information</h3>
            <div className="space-y-3">
              {[
                { label: 'Role',         value: 'School Administrator' },
                { label: 'School ID',    value: user?.schoolId || 'N/A' },
                { label: 'Member since', value: 'January 2025' },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center">
                  <p className={`text-xs font-medium ${mutedCls}`}>{label}</p>
                  <p className={`text-xs font-semibold ${bodyTxtCls}`}>{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className={`${cardCls} p-5 ${dm ? 'border-red-900/40' : 'border-red-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-sm font-semibold ${dm ? 'text-red-400' : 'text-red-600'}`}>Danger Zone</h3>
            <p className={`text-xs mt-0.5 ${descCls}`}>Irreversible administrative actions — proceed with caution.</p>
          </div>
          <div className="flex gap-2">
            <button className="btn-glass btn-glass-pill btn-glass-danger text-sm">Export all school data</button>
            <button className="btn-glass btn-glass-pill btn-glass-danger text-sm">Archive school account</button>
          </div>
        </div>
      </div>
    </div>
  );
}
